import { Stack, Theme, Typography, useTheme } from "@mui/material";
import { QueryStatus } from "@reduxjs/toolkit/query";

import { useUnitsFormatter } from "hooks/formatters";
import { useNumberSerialiser } from "hooks/serialisers/numbers";

import {
  UnitType,
  useSummarisePlanResultsMutation,
} from "src/store/api/generatedApi";
import { PlanId } from "../boundary/PlanId";
import { Period } from "hooks/periodIndex";

interface Props {
  planId: PlanId;
  period: Period;
  mixId: number;
  originalPrice: number;
}

const NoDiff = () => {
  return (
    <Typography
      component="span"
      variant="body2Mono"
      sx={{
        paddingX: 1,
      }}
    >
      -
    </Typography>
  );
};

export const PriceDiffDisplay = ({
  priceDiff,
  unitType,
}: {
  priceDiff: number;
  unitType: UnitType;
}) => {
  const theme = useTheme();
  const { format: priceFormat } = useNumberSerialiser({
    decimalPlaces: 2,
    min: 0,
  });
  const unit = useUnitsFormatter(false);

  const backgroundColor: Theme["palette"]["success"]["main"] = (() => {
    switch (true) {
      case priceDiff < 0:
        return theme.palette.success.main;
      case priceDiff > 0:
        return theme.palette.error.main;
      default:
        return theme.palette.grey[600];
    }
  })();

  return (
    <Typography
      component="span"
      variant="body2Mono"
      sx={{
        backgroundColor,
        color: theme.palette.common.white,
        paddingX: 1,
        paddingY: 0.5,
        borderRadius: 0.5,
        lineHeight: 1,
      }}
    >
      {priceDiff >= 0 ? "+" : ""}
      {priceFormat(priceDiff)}
      <Typography component="span" variant="body2Mono" sx={{ lineHeight: 1 }}>
        {unit(unitType)}
      </Typography>
    </Typography>
  );
};

const DynamicPriceDiff = ({ planId, period, originalPrice, mixId }: Props) => {
  const [, summariseMutationResult] = useSummarisePlanResultsMutation({
    fixedCacheKey: `${planId}-${period}`,
  });

  switch (summariseMutationResult.status) {
    case QueryStatus.rejected: {
      throw new Error(
        `MixPriceDiff: Price calculation for plan ${planId} failed`
      );
    }
    case QueryStatus.uninitialized: {
      return null;
    }
    case QueryStatus.fulfilled: {
      const result = summariseMutationResult.data.mixes.find(
        (result) => result.original_mix_id === mixId
      );
      if (result?.mix.summary) {
        const {
          mix: {
            summary: {
              cost: { display_cost_per_tonne },
            },
          },
        } = result;

        if (display_cost_per_tonne.toFixed(2) === originalPrice.toFixed(2)) {
          return <NoDiff />;
        }

        return (
          <PriceDiffDisplay
            priceDiff={display_cost_per_tonne - originalPrice}
            unitType="specific_cost"
          />
        );
      }
      return null;
    }
    case QueryStatus.pending: {
      return <NoDiff />;
    }
  }
};

export const MixPriceDiff = (props: Props) => {
  const { planId, period, mixId, originalPrice } = props;

  return (
    <Stack direction="column" alignItems="center" justifyContent="center">
      <DynamicPriceDiff
        planId={planId}
        period={period}
        mixId={mixId}
        originalPrice={originalPrice}
      />
    </Stack>
  );
};
